import React, {useEffect, useRef, useState} from 'react';
import {useOrganizationService} from "../../../services";
import {useJwt} from "../../../helpers/common";
import {useDispatch, useSelector} from 'react-redux';
import {HiPlusCircle} from "react-icons/hi";
import {setShowSkillEdit, setOrganizationOnboardSettings} from '../../../redux/slices/organization';
import DataTable from '../../../components/Tables/DataTable';
import CustomButton from '../../../components/common/CustomButton';
import OrgSkillsModal from './OrgSkillsModal';
import ConfirmDeleteModal from "../../../components/Modals/ConfirmDeleteModal";
import {
  OnboardingSpotlight,
  OnboardingStepKeyEnum,
  useOnboardingFlattenedTour,
  useOnboardingNavigator,
  useOnboardingRestart,
  useOnboardingRoadmap,
  useOnboardingStep
} from '../../../components/Onboarding';

const Skills = () => {
  const dispatch = useDispatch()
  const [currentData, setCurrentData] = useState<any>({});
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const JWTdata:any = useJwt();
  const org_id = JWTdata.org_id;
  const skillsData = useSelector((state: any) => state.organization.skills);
  const skillLoading = useSelector((state: any) => state.organization.skillLoading);
  const showSkillEdit = useSelector((state: any) => state.organization.showSkillEdit);
  const { deleteOrganizationSkill, updateOrganization } = useOrganizationService();
  const { onboardSettings = {} } = useSelector((s: any) => s.organization);
  const [sortConfig, setSortConfig] = useState<{ key: string; direction: 'asc' | 'desc' }>({
    key: 'skill',
    direction: 'asc'
  });

  const [showSkillModal, setShowSkillModal] = useState(false);
  const addSkillWrapperRef = useRef<HTMLDivElement>(null);
  const { isCurrent } = useOnboardingRoadmap();
  const { 
    show: showAddSkillTour, 
    complete: completeAddSkill 
  } = useOnboardingStep(OnboardingStepKeyEnum.OrgSkills, { shouldShow:true });
  const { totalSteps: globalTotal, getIndexForStepKey } = useOnboardingFlattenedTour();
  const { goToNextFrom } = useOnboardingNavigator();
  const restartToken = useOnboardingRestart(OnboardingStepKeyEnum.OrgSkills);

  const sortData = (data: any[], key: string) => {
    return [...data].sort((a, b) => {
      if (a[key] < b[key]) {
        return sortConfig.direction === 'asc' ? -1 : 1;
      }
      if (a[key] > b[key]) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });
  };

  const handleSort = (key: string) => {
    setSortConfig({
      key,
      direction: sortConfig.key === key && sortConfig.direction === 'asc' ? 'desc' : 'asc'
    });
  };

  const sortedSkillsData = sortData(skillsData, sortConfig.key);

    useEffect(()=>{
      if(org_id){
        dispatch(setShowSkillEdit(false))
      }
    },[org_id]);

    const handleEdit = (data:any) => {
      setCurrentData(data)
      dispatch(setShowSkillEdit(true))
      setShowSkillModal(true)
    }
    const handleAdd = () => {
      dispatch(setShowSkillEdit(true))
      setShowSkillModal(true)
    }
    const cancelAddUpadte = () => {
      dispatch(setShowSkillEdit(false))
      setCurrentData({})
      setShowSkillModal(false)
    }

    const handleDelete = (status:boolean) => {
      setShowDeleteConfirm(status)
    }

    const submitDelete = () => {
      if(currentData?.id){
        const skillId = currentData.id.toString();
        deleteOrganizationSkill(org_id, skillId);
        setCurrentData({})
        setShowDeleteConfirm(false)
    }
  }

  return (
    <div className="flex-1 row bg-light-surfacePrimary dark:bg-tertiary-dark w-full h-full">
      <div className="p-8  mx-auto w-full divide-y dark:divide-gray-700 h-full flex flex-col">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between md:space-y-0 md:space-x-4 mb-4">
          <div className="flex items-start flex-1 mb-2 space-x-4">
            <div className="flex flex-col flex-1 gap-1">
              <h4 className="text-xl font-bold text-gray-900 dark:text-white font-['Inter']">
                Organization Skills
              </h4>
              <p className="text-sm font-semibold text-gray-500 dark:text-gray-400 font-['Inter']">
                Create and manage organization skills based on company domain.
              </p>
            </div>
          </div>
          {!showSkillEdit && (
            <div className="w-full md:w-auto flex justify-end addNewSkill" ref={addSkillWrapperRef}>
              <CustomButton
                onClick={handleAdd}
                size="md"
                leftIcon={<HiPlusCircle className="text-xl" />}
              >
                Add new Skill
              </CustomButton>
            </div>
          )}
        </div>
        <div className="flex-1 w-full h-full flex overflow-hidden">
          <div className="mt-8 flex-1">
            <DataTable
              data={sortedSkillsData}
              columns={skillColumns}
              sortConfig={sortConfig}
              handleSort={handleSort}
              handleEdit={handleEdit}
              loading={skillLoading}
              skeletonRows={10}
              tableType="skills"
            />
          </div>
        </div>
      </div>
      <OrgSkillsModal
        isOpen={showSkillModal}
        onClose={cancelAddUpadte}
        currentData={currentData}
      />
      {showAddSkillTour && !showSkillEdit && (
        <OnboardingSpotlight
          key={`orgskills-${restartToken}`}
          targetRef={addSkillWrapperRef}
          message="Create a skill (React, QA, DevOps) for task and user allocation."
          onDismiss={() => {
            goToNextFrom(OnboardingStepKeyEnum.OrgSkills); 
            completeAddSkill();
          }}
          onCompleteOnly={() => {
            completeAddSkill();
          }}
          onClose={async () => {
            const next = { ...(onboardSettings || {}), dismissed: true };
            dispatch(setOrganizationOnboardSettings(next));
            await updateOrganization(JWTdata?.org_id, { onboard_settings: next }, true);
          }}
          variant="spotlight"
          stepIndex={Math.max(0, getIndexForStepKey(OnboardingStepKeyEnum.OrgSkills))}
          totalSteps={globalTotal}
          onBack={() => { /* dismiss-only; previous steps are managed in their own views */ }}
          onSkip={() => { completeAddSkill(); }}
        />
      )}
      <ConfirmDeleteModal
          showModal={showDeleteConfirm} 
          setShowModal={handleDelete} 
          handleConfirm={submitDelete} 
          title="Delete Skill"
          description={`Please make sure to change this skill if it is already being used in an existing task.`}
      />
    </div>
  )
}

const skillColumns = [
  { key: 'skill', label: 'Skill Name',
    width: 'w-[25%]',
    render: (value: string) => (
      <div className="w-48 truncate" title={value}>{value}</div>
    )
  },
  { 
    key: 'description', 
    label: 'Job Duties',
    width: 'w-[25%]',
    render: (value: string) => (
      <div className="w-48 truncate" title={value}>{value}</div>
    )
  },
  { 
    key: 'cost_per_hour', 
    label: 'Skill Cost',
    width: 'w-[20%]',
    render: (value: number) => typeof value === 'number' ? `$ ${value}` : ""
  },
  { 
    key: 'billing_per_hour', 
    label: 'Skill Billing',
    width: 'w-[15%]',
    render: (value: number) => typeof value === 'number' ? `$ ${value}` : ""
  },
  { 
    key: 'color_code', 
    label: 'Skill Color',
    width: 'w-[15%]',
    render: (value: string) => (
      <div className="w-14 rounded-full" style={{background: value}}>
        <span className="text-[1px]">.</span>
      </div>
    )
  }
];

export default Skills;